import { useEffect, useMemo, useState } from "react";
import { Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import FilterSearch from "../../../components/Filtering/FilterSearch";
import SearchButton from "../../../components/buttons/SearchButton";
import DataTable from "../../../components/DataTable/DataTable";
import { DetailsPopover } from "../../../components/DataTable/DetailsPopover";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useFilteredData } from "../../../hooks/useFilteredData";
import { useTableControls } from "../../../hooks/useTableControls";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { useGetTransfer } from "../../../hooks/contracts/useGetTransfer";
import { usePostContractAgreementRequest } from "../../../hooks/contracts/usePostContractAgreementRequest";
import { useGetDistributionsByIds } from "../../../hooks/distributions/useGetDistributionsByIds";
import { getTextByLang } from "../../../utils/formUtils";
import { iriLast } from "../../../utils/iriLast";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import {
  buildParticipantsById,
  distributionIdFromItem,
  formatUnixDate,
  getContractParticipantLabel,
  listFromResponse,
  normalizeContract,
} from "./utils";
import { useGetParticipantsOld } from "../../../hooks/participants/useGetParticipantsOld";
import { resolveParticipantConnectorOrigin } from "../../../utils/auth/adminConnectorRuntime";

const NOT_SPECIFIED_TEXT = "Not specified.";

function validDisplayText(value) {
  const text = String(value ?? "").trim();
  return text && text !== NOT_SPECIFIED_TEXT ? text : "";
}

function resolveDistributionTitle(distribution, language) {
  if (!distribution) return "-";

  return (
    validDisplayText(getTextByLang(distribution.title, language)) ||
    validDisplayText(getTextByLang(distribution.title, "es")) ||
    validDisplayText(getTextByLang(distribution.title, "en")) ||
    "-"
  );
}

export default function ContractsTable() {
  const { t, i18n } = useTranslation();
  const { callApiWithWorker } = useWorkerApiHelper();
  const { isAdmin, isConsumer, token } = useAuthRoles();
  const [loadError, setLoadError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const canSeeDownloadAction = isAdmin || isConsumer;
  const {
    tableData,
    setTableData,
    searchTerm,
    pendingSearchTerm,
    pagination,
    setPagination,
    rowCount,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
  } = useTableControls([]);

  const contractsMut = usePostContractAgreementRequest({
    callApiWithWorker,
    t,
    useProviderConnector: isAdmin,
    showSuccess: false,
  });
  const transferMut = useGetTransfer({
    callApiWithWorker,
    t,
  });
  const { mutateAsync: loadContractsRequest, isPending } = contractsMut;

  const distributionIds = useMemo(
    () => [...new Set(tableData.map((item) => item.assetId).filter(Boolean))],
    [tableData],
  );

  const distributionsQ = useGetDistributionsByIds({
    callApiWithWorker,
    ids: distributionIds,
    language: i18n.language,
    enabled: distributionIds.length > 0,
  });
  const participantsQ = useGetParticipantsOld({
    callApiWithWorker,
    lang: i18n.language,
    pageIndex: 1,
    pageSize: 1000,
    status: undefined,
  });

  useEffect(() => {
    let active = true;

    async function loadContracts() {
      setLoadError(null);

      try {
        const response = await loadContractsRequest({});
        if (!active) return;

        const rows = listFromResponse(response).map(normalizeContract);
        setTableData(rows);
        setRowCount(rows.length);
      } catch (error) {
        if (!active) return;
        setLoadError(error);
        setTableData([]);
        setRowCount(0);
        showErrorAlert(error, t);
      }
    }

    loadContracts();

    return () => {
      active = false;
    };
  }, [loadContractsRequest, setRowCount, setTableData, t]);

  const distributionsById = useMemo(() => {
    const items = Array.isArray(distributionsQ.data) ? distributionsQ.data : [];
    return Object.fromEntries(
      items
        .map((item) => [distributionIdFromItem(item), item])
        .filter(([id]) => Boolean(id)),
    );
  }, [distributionsQ.data]);

  const participantsById = useMemo(
    () => buildParticipantsById(participantsQ.data?.content),
    [participantsQ.data?.content],
  );

  const enrichedRows = useMemo(
    () =>
      tableData.map((row) => {
        const distribution = row.assetId
          ? distributionsById[row.assetId]
          : null;

        const distributionTitle = resolveDistributionTitle(
          distribution,
          i18n.language,
        );

        return {
          ...row,
          name: distributionTitle,
          providerId: row.provider,
          provider: getContractParticipantLabel(
            row.provider,
            participantsById,
            i18n.language,
          ),
          consumer: getContractParticipantLabel(
            row.consumer,
            participantsById,
            i18n.language,
          ),
          distributionFormat: distribution?.format || null,
        };
      }),
    [distributionsById, i18n.language, participantsById, tableData],
  );

  const columns = useMemo(
    () => [
      {
        header: t("privateArea.mycontract.name"),
        accessorKey: "name",
        meta: { width: "30%" },
      },
      {
        header: t("roles.provider"),
        accessorKey: "provider",
        meta: { width: "20%" },
      },
      {
        header: t("roles.consumer"),
        accessorKey: "consumer",
        meta: { width: "25%" },
      },
      {
        header: t("privateArea.mycontract.signingDate"),
        accessorKey: "contractSigningDate",
        meta: { width: "25%" },
        cell: ({ getValue }) => formatUnixDate(getValue()),
      },
    ],
    [t],
  );

  const filteredData = useFilteredData(enrichedRows, searchTerm, {}, columns);

  const handleDownload = async (row) => {
    if (!row?.assetId || !row?.contractAgreementId) return;

    const ext = row?.distributionFormat
      ? iriLast(row.distributionFormat).toLowerCase()
      : "bin";

    try {
      const urlProvider = await resolveParticipantConnectorOrigin(
        row.providerId,
        token,
      );
      await transferMut.mutateAsync({
        identifier: row.assetId,
        datasetName: row.name,
        contractAgreementId: row.contractAgreementId,
        filename: `distribution-${row.assetId}.${ext}`,
        urlProvider,
      });
    } catch {}
  };

  const detailsFields = useMemo(
    () => [
      {
        key: "name",
        label: t("privateArea.mycontract.name"),
      },
      {
        key: "provider",
        label: t("roles.provider"),
      },
      {
        key: "consumer",
        label: t("roles.consumer"),
      },
      {
        key: "contractSigningDate",
        label: t("privateArea.mycontract.signingDate"),
        render: (value) => formatUnixDate(value),
      },
    ],
    [t],
  );

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>

      <div className="container containerflex">
        <div className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("privateArea.mycontract.title")}</h2>
        </div>

        <div className="w86 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>

        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            {isPending ||
            distributionsQ.isLoading ||
            participantsQ.isLoading ? (
              <div className="loading">
                <Spinner size="large" color="var(--main-color)" />
              </div>
            ) : loadError ? (
              <Typography color="error">{loadError?.message || "-"}</Typography>
            ) : (
              <DataTable
                columns={columns}
                data={filteredData}
                onDetails={(row) => {
                  setSelectedRow(row);
                  setDetailsOpen(true);
                }}
                showActions={true}
                pagination={pagination}
                setPagination={setPagination}
                rowCount={rowCount}
              />
            )}
          </div>
        </div>
      </div>

      <DetailsPopover
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={t("privateArea.actionLog.rowDetails")}
        data={selectedRow}
        fields={detailsFields}
        actions={
          canSeeDownloadAction ? (
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: "var(--main-color)",
                color: "#FFFFFF",
                border: "1px solid var(--main-color)",
                textTransform: "none",
                fontWeight: 500,
                minWidth: "120px",
                "&:hover": {
                  backgroundColor: "var(--secondary-color)",
                  borderColor: "var(--secondary-color)",
                },
              }}
              onClick={() => handleDownload(selectedRow)}
              disabled={
                !selectedRow?.assetId ||
                !selectedRow?.contractAgreementId ||
                (transferMut.isPending &&
                  transferMut.variables?.identifier === selectedRow?.assetId)
              }
            >
              {transferMut.isPending &&
              transferMut.variables?.identifier === selectedRow?.assetId
                ? t("common.loading") || "Loading..."
                : t("common.download")}
            </Button>
          ) : null
        }
      />
    </section>
  );
}
